/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { func, object, string } from 'prop-types';
import CreateIcon from '@material-ui/icons/Create';

import {
    enableEditButton, giftText, removeUSZipCodeSegment, scrollToId,
} from '../helper';
import configENV from '../../../config';
import FrequencyDuration from './FrequencyDuration';
import RecipientDialog from '../Dialogs/RecipientDialog';
import { clearErrors } from '../../../../../../state/ducks/Common/ducks/Error/Error-Actions';
import SubscriptionEditGiftMessage from '../SubscriptionGiftMessage/SubscriptionEditGiftMessage';
import SubscriptionEditGiftMsgDialog from '../../Subscriptions/SubsriptionGiftMessage/SubscriptionEditGiftMsgDialog';
import { clearRecipientFormFields } from '../../../../../../state/ducks/Recipient/ducks/RecipientForm/RecipientForm-Actions';
import MaskInput from '../../Subscriptions/Partials/TextFieldWrapper/MaskInput';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import { isEmpty } from '../../../utils/object';
import SubscriptionTnC from './SubscriptionTnC';

const { PERSIST_KEY } = configENV;

const useStyles = makeStyles({
    container: {
        width: '100%',
        '& div, & p, & h1, & h2, & h3': {
            fontFamily: 'Lato, sans-serif',
        },
    },
    details: {
        fontSize: '16px',
        lineHeight: '24px',
        color: '#000',
        borderBottom: '1px solid #D9D9D9',
        '@media screen and (max-width: 1025px)': {
            borderBottom: 'none',
            borderTop: '1px solid #D9D9D9',
        },
        '& ._heading': {
            fontWeight: '700',
            letterSpacing: '0.16px',
            padding: '24px 0 16px',
        },
        '& ._delivery_date': {
            '@media screen and (min-width: 1024px)': {
                paddingTop: '0 !important',
            },
        },
        '& ._detail_container': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            paddingBottom: '24px',
            '@media screen and (max-width: 1025px)': {
                fontSize: '14px',
            },
            '& > div:first-child': {
                width: '100%',
            },
        },
        '& ._t_and_c': {
            fontSize: '14px',
            lineHeight: '20px',
            paddingTop: '16px',
            paddingBottom: '24px',
            letterSpacing: '0.14px',
            '& span': {
                cursor: 'pointer',
                textDecoration: 'underline',
            },
        },
    },
    btnContainer: ({ brandColor }) => ({
        margin: '-4px 0 24px',
        '& .MuiButton-root': {
            fontSize: '14px',
            fontWeight: '700',
            lineHeight: '20px',
            letterSpacing: '0.56px',
            borderRadius: '3px',
            border: `1px solid ${brandColor || '#201648'}`,
            padding: '8px 20px',
            color: brandColor || '#201648',
            background: 'transparent',
            boxShadow: 'none',
            textTransform: 'capitalize',
            '@media screen and (max-width: 480px)': {
                padding: '8px',
            },
        },
        '& > button:nth-child(2), & > button:nth-child(3)': {
            marginLeft: '12px',
            '@media screen and (max-width: 620px)': {
                marginLeft: '16px',
            },
            '@media screen and (max-width: 1340px) and (min-width: 1025px)': {
                marginLeft: '16px',
            },
        },
    }),
    editButton: ({ brandColor }) => ({
        fontSize: '16px',
        padding: '0 8px 0 35px',
        textAlign: 'right',
        fontFamily: 'Lato',
        lineHeight: '24px',
        textDecoration: 'underline',
        color: brandColor || '#201648',
    }),
    editIcon: ({ brandColor }) => ({
        width: '18px',
        color: brandColor || '#201648',
    }),
    subContainer: {
        display: 'flex',
        cursor: 'pointer',
    },
    recurrenceContainer: {
        borderBottom: 'none !important',
        '& ._heading': {
            paddingBottom: '0px !important',
        },
    },
});

const ProductSummary = ({
    data,
    handleModal,
    trackEvent,
    featureFlags,
    customerId,
    validatedZip,
    brandColor,
}) => {
    const classes = useStyles({ brandColor });
    const dispatch = useDispatch();
    const shortTextDT = useMediaQuery('(max-width: 1340px) and (min-width:1024px)');
    const shortTextMB = useMediaQuery('(max-width: 620px)');
    const [toggleForm, setToggleForm] = useState('');
    const [openTnC, setOpenTnC] = useState(false);
    const subscriptions = useSelector((state) => state[PERSIST_KEY]?.subscriptions) || [];
    const subscriptionMessages = useSelector((state) => state[PERSIST_KEY]?.subscriptionMessages) || [];
    const subscriptionsGiftMessage = subscriptionMessages.find((message) => message.id === data.ruleId)?.text || '';

    useEffect(() => {
        dispatch(EventDispatcher.clearSubscriptionGiftMessage());
        dispatch(EventDispatcher.fetchSubscriptionGiftMessage(data.ruleId));
    }, []);

    const handleEditButtonAction = (event) => {
        trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click',
            eventLabel: `Edit - ${event}`,
        });
        switch (event) {
            case 'CANCEL':
                handleModal('Cancel', data.ruleId);
                break;

            case 'RECIPIENT':
                setToggleForm('Recipient');
                break;

            case 'GIFT_MESSAGE':
                setToggleForm('Gift_Message');
                break;

            case 'FREQUENCY_AND_DURATION':
                handleModal('Frequency & Duration', data.ruleId);
                break;

            default:
                break;
        }
    };

    const closeClick = (buttonClicked) => {
        trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click – Edit Recipient Modal',
            eventLabel: `${buttonClicked || 'Save'}`,
        });

        setToggleForm('');
        dispatch(clearErrors({
            errType: 'recipientFormClearAllFields',
        }));
        dispatch(clearRecipientFormFields());
        const id = `_Product_${data.ruleId}`;
        setTimeout(() => scrollToId({ id }), 250);
    };

    const handleEditGiftClose = (cancelledBool) => {
        if (cancelledBool) {
            trackEvent({
                eventCategory: 'Subscriptions Management Portal',
                eventAction: 'click – Edit Card Message Modal',
                eventLabel: 'Cancel',
            });
        }
        setToggleForm('');
    };

    const renderChangeButton = (event, subscription, btn, noIcon) => (
        <div
            className={classes.subContainer}
            onClick={() => handleEditButtonAction(event, subscription)}
            aria-hidden="true"
        >
            <span className={classes.editButton}>{btn || 'Edit'}</span>
            {!noIcon && <CreateIcon className={classes.editIcon} />}
        </div>
    );

    const updateSubscriptionReduxList = (recipientData = {}) => {
        if (isEmpty(recipientData)) return;
        const subscriptionsList = [...subscriptions];
        const index = subscriptionsList.findIndex((sub) => sub.ruleId === data.ruleId);
        if (index > -1) {
            const recipient = { ...recipientData };
            recipient.street1 = recipientData.address1;
            recipient.street2 = recipientData.address2;
            recipient.zipcode = recipientData.zipCode;
            recipient.telephoneNumber = recipientData.phone;
            recipient.firstAndLastName = `${recipientData.firstName} ${recipientData.lastName}`;
            subscriptionsList[index].recipient = [{ ...subscriptionsList[index].recipient[0], ...recipient }];
            dispatch(EventDispatcher.updateSubscriptionList(subscriptionsList));
        }
    };

    const recipientData = data.recipient?.[0] || {};
    const recipientAddressLine1 = recipientData.street1 || recipientData.address1;
    const recipientAddressLine2 = recipientData.street2 || recipientData.address2;
    const recipientZipCode = recipientData.zipcode || recipientData.zipCode;
    const organizationName = recipientData.organizationName || '';
    const telephoneNumber = recipientData.telephoneNumber || '';
    const PAY_UP_FRONT_ORDER = data?.subscriptionType?.toLowerCase() === 'monthonlypayfull';
    const [currentMessage, signatureMessage] = giftText(subscriptionsGiftMessage);
    const isActiveSubscription = ['PROCESSED', 'UPDATED', 'SKIPPED', 'RESUMED'].includes(data.subscriptionStatus) || false;

    let nextDate = 'N/A';
    if (isActiveSubscription && data.nextOrderDate) nextDate = data.nextOrderDate;
    else if (!isActiveSubscription && data?.orderHistoryResult?.[0]?.deliveryDate) nextDate = format(new Date(data?.orderHistoryResult?.[0]?.deliveryDate), 'MMM dd, yyyy');

    return (
        <div className={classes.container}>
            <div className={classes.details}>
                <div className="_heading _delivery_date">{isActiveSubscription ? 'Next Order' : 'Last Delivery'} Date:</div>
                <div className="_detail_container">
                    <div>
                        <div className="_value">{nextDate}</div>
                    </div>
                    {/* {enableEditButton(data) && renderChangeButton('', data)} */}
                </div>
                {enableEditButton(data) && (
                    <div className={classes.btnContainer}>
                        {isActiveSubscription
                            ? (
                                <>
                                    <Button
                                        disabled={PAY_UP_FRONT_ORDER}
                                        variant="contained"
                                        onClick={() => handleModal('Pause', data.ruleId)}
                                    >
                                        {(shortTextDT || shortTextMB) ? 'Pause' : 'Pause Subscription'}
                                    </Button>
                                    <Button
                                        disabled={PAY_UP_FRONT_ORDER}
                                        variant="contained"
                                        onClick={() => handleModal('Skip', data.ruleId)}
                                    >
                                        {(shortTextDT || shortTextMB) ? 'Skip' : 'Skip Next Order'}
                                    </Button>
                                </>
                            )
                            : (
                                <Button
                                    disabled={PAY_UP_FRONT_ORDER}
                                    variant="contained"
                                    onClick={() => handleModal('Resume', data.ruleId)}
                                >
                                    {(shortTextDT || shortTextMB) ? 'Resume' : 'Resume Subscription'}
                                </Button>
                            )}
                        <Button
                            disabled={PAY_UP_FRONT_ORDER}
                            variant="contained"
                            onClick={() => handleModal('Cancel', data.ruleId)}
                        >
                            {(shortTextDT || shortTextMB) ? 'Cancel' : 'Cancel Subscription'}
                        </Button>
                    </div>
                )}
            </div>
            <div className={classes.details}>
                <div className="_heading">Recipient:</div>
                <div className="_detail_container">
                    <div>
                        <div className="_value">
                            <div className={classes.recipientName}>{recipientData.firstName}{' '}{recipientData.lastName}</div>
                            {organizationName && <div>{organizationName}</div>}
                            <div>{recipientAddressLine1}{' '}{recipientAddressLine2 ? `, ${recipientAddressLine2}` : ''}</div>
                            <div>{`${recipientData.city}, ${recipientData.state} ${removeUSZipCodeSegment(recipientZipCode)}`}</div>
                            {telephoneNumber && <div>{MaskInput.Phone(telephoneNumber)?.maskInputVal}</div>}
                        </div>
                    </div>
                    {enableEditButton(data) && renderChangeButton('RECIPIENT', data)}
                    {Object.keys(recipientData).length > 0 && (
                        <RecipientDialog
                            ruleId={data.ruleId}
                            selectedSubscription={data}
                            orderItemId={data.productId}
                            closeClick={closeClick}
                            recipientData={recipientData}
                            customerId={customerId}
                            featureFlags={featureFlags}
                            updateSubscriptionReduxList={updateSubscriptionReduxList}
                            validatedZip={validatedZip}
                            toggleForm={toggleForm}
                        />
                    )}
                </div>
            </div>
            <div className={classes.details}>
                <div className="_heading">Gift Message:</div>
                <div className="_detail_container">
                    <div>
                        <div className="_value">
                            {(currentMessage || signatureMessage) ? currentMessage : 'No Card Message'}
                            {signatureMessage && currentMessage ? <br /> : ''}
                            {signatureMessage && `From: ${signatureMessage}`}
                        </div>
                    </div>
                    {enableEditButton(data) && renderChangeButton('GIFT_MESSAGE', data)}
                </div>
            </div>
            <div className={classes.details}>
                <div className="_heading">Payment:</div>
                <div className="_detail_container">
                    <div>
                        <div className="_value">
                            Your Default Card will be charged
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${classes.details} ${classes.recurrenceContainer}`}>
                <div className="_heading">Recurrence:</div>
                <div className="_detail_container">
                    <div>
                        <div className="_value">
                            <div>
                                <FrequencyDuration
                                    subscription={data}
                                    brandColor={brandColor}
                                    subscriptions={subscriptions}
                                    enableEditButton={enableEditButton}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* {enableEditButton(data) && (
                <div className={`${classes.details} ${classes.recurrenceContainer}`} style={{ paddingBottom: '16px', border: 'none' }}>
                    <div className="_detail_container">
                        <div>
                            <div className="_value">
                                Looking to cancel your subscription?
                            </div>
                        </div>
                        {renderChangeButton('CANCEL', data, 'Cancel', 'noIcon')}
                    </div>
                </div>
            )} */}
            <div className={`${classes.details} ${classes.recurrenceContainer}`} style={{ paddingBottom: '16px', border: 'none' }}>
                <div className="_t_and_c">
                    See <span key={1} role="link" tabIndex={0} onClick={() => setOpenTnC(true)} onKeyDown={() => {}}>Subscription Program Terms and Conditions</span>
                </div>
            </div>
            {toggleForm === 'Gift_Message' && !featureFlags['is-manage-subscriptions-gift-message-v2'] ? (
                <SubscriptionEditGiftMsgDialog
                    isOpen={toggleForm === 'Gift_Message'}
                    selectedSubscription={data}
                    subscriptionsGiftMessage={`${currentMessage}${signatureMessage ? ` From: ${signatureMessage}` : ''}`}
                    ruleId={data.ruleId}
                    handleEditGiftClose={handleEditGiftClose}
                />
            ) : (
                <SubscriptionEditGiftMessage
                    isOpen={toggleForm === 'Gift_Message'}
                    handleClose={handleEditGiftClose}
                    giftMessage={currentMessage}
                    signatureMessage={signatureMessage}
                    selectedSubscription={data}
                />
            )}
            {openTnC && <SubscriptionTnC subscription={data} open={openTnC} toggle={setOpenTnC} />}
        </div>
    );
};

ProductSummary.defaultProps = {
    trackEvent: () => { },
    validatedZip: '',
};

ProductSummary.propTypes = {
    data: object.isRequired,
    trackEvent: func,
    handleModal: func.isRequired,
    featureFlags: object.isRequired,
    customerId: string.isRequired,
    validatedZip: string,
    brandColor: string.isRequired,
};

export default ProductSummary;
